import { Component } from '@theme/component';
import { selectVideoSource } from './video-source.js';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];
  #loadObserver;
  #playObserver;
  #loaded = false;
  #visible = false;

  #load = () => {
    if (this.#loaded || !this.getClientRects().length) return;
    this.#loaded = true;
    const { videoSources, videoElement } = this.refs;
    selectVideoSource(videoElement);
    for (const source of videoSources) {
      if (!videoElement.contains(source)) continue;
      if (source.dataset.videoSource) source.setAttribute('src', source.dataset.videoSource);
    }
    videoElement.preload = 'metadata';
    videoElement.load();
    this.#loadObserver?.disconnect();
  };

  #syncPlayback = () => {
    const { videoElement } = this.refs;
    if (this.#visible && !document.hidden) {
      this.#load();
      videoElement.play().catch(() => {});
    } else videoElement.pause();
  };

  connectedCallback() {
    super.connectedCallback();

    if ('IntersectionObserver' in window) {
      this.#loadObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.#load();
      }, { rootMargin: '0px' });
      this.#loadObserver.observe(this);
      this.#playObserver = new IntersectionObserver(([entry]) => {
        this.#visible = entry.isIntersecting;
        this.#syncPlayback();
      });
      this.#playObserver.observe(this);
    } else {
      this.#visible = true;
      this.#syncPlayback();
    }
    document.addEventListener('visibilitychange', this.#syncPlayback);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#loadObserver?.disconnect();
    this.#playObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.#syncPlayback);
    this.refs.videoElement?.pause();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
